import { Checkbox, FormControlLabel } from '@material-ui/core';
import { RulesConfigurationFormType } from '../../types/types';
import TrafficLightInputs from './TrafficLightInputs';
import { PolygonZone } from 'shared/models/PolygonZones';
import { Button } from '@vadiun/react-components';
import { useState } from 'react';
import { useFormikContext } from 'formik';
import MessagesConfigurationsModal from './MessagesConfigurationsModal';
import { ChannelType } from 'shared/models/ChannelTyoes';

interface TravelStatusTimelineProps {
  orderedStates: string[];
  readOnly: boolean;
  polygonZonesOptions: PolygonZone[];
  channels: ChannelType[];
}

const EMPTY_MESSAGE = {
  id: undefined as number | undefined,
  channel_keys: [] as string[],
  title: '',
  body: '',
};

/** Misma forma que al agregar etapas en TravelStatusStep (value + message por color). */
const createTrafficLightRow = (polygon_zone_id?: number) => ({
  ...(polygon_zone_id !== undefined ? { polygon_zone_id: polygon_zone_id } : {}),
  semaphore_levels: [
    { semaphore_color_key: 'green', threshold_minutes: 0, semaphore_message_templates: { ...EMPTY_MESSAGE } },
    { semaphore_color_key: 'yellow', threshold_minutes: 0, semaphore_message_templates: { ...EMPTY_MESSAGE } },
    { semaphore_color_key: 'orange', threshold_minutes: 0, semaphore_message_templates: { ...EMPTY_MESSAGE } },
    { semaphore_color_key: 'red', threshold_minutes: 0, semaphore_message_templates: { ...EMPTY_MESSAGE } },
  ]
});

const Circle = ({ travelStatus }: { travelStatus: string }) => {
  return (
    <div className="relative z-10 h-8 w-8 index-10 rounded-full border-2 border-secondary-400" >
      <div className="absolute h-6 w-6 rounded-full bg-secondary-600 " style={{
        top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        zIndex: 0,
      }} />
      <span className="absolute whitespace-nowrap text-sm font-medium text-secondary-700" style={{ top: '-25px', left: '50.0%', transform: 'translateX(-50.0%)' }}>
        {travelStatus}
      </span>
    </div>
  );
};

const TravelStatusTimeline = ({
  orderedStates,
  readOnly,
  polygonZonesOptions,
  channels }: TravelStatusTimelineProps) => {
  const [messagesModalIndex, setMessagesModalIndex] = useState<number | null>(null);
  const { values, setFieldValue } = useFormikContext<RulesConfigurationFormType>();
  return (
    <>
      {orderedStates.map((travelStatus, index) => (
        <div key={index} className="flex flex-col items-center gap-2 w-fit">
          {index > 0 ? (
            <div
              className="animate-in-left flex w-full h-full items-center"
              style={{ width: '100%' }}
            >
              <div className="relative flex-1 flex flex-col items-center justify-center gap-0 h-full">
                <div
                  className="animate-line-grow w-full bg-secondary-400"
                  style={{
                    minWidth: '190px',
                    height: '2px',
                  }}
                />
                <div className="absolute top-5 flex flex-col rounded-lg bg-white p-2 border border-borderInputSection z-20">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          values.stages[index - 1]
                            .is_countdown
                        }
                        onChange={(e) =>
                          setFieldValue(
                            `stages[${index - 1}].is_countdown`,
                            e.target.checked
                          )
                        }
                        color="primary"
                        disabled={readOnly}
                      />
                    }
                    label={
                      <span className="text-[10px] text-gray-500 uppercase font-bold">
                        Cuenta regresiva
                      </span>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={
                          values.stages[index - 1]
                            .has_polygon
                        }
                        onChange={(e) => {
                          if (readOnly) return;
                          const hasPoly = e.target.checked;
                          setFieldValue(
                            `stages[${index - 1}].has_polygon`,
                            hasPoly
                          );

                          if (hasPoly) {
                            setFieldValue(
                              `stages[${index - 1}].semaphores`,
                              polygonZonesOptions
                                .sort((a, b) => a.id - b.id)
                                .map((polygonZone) => createTrafficLightRow(polygonZone.id))
                            );
                          } else {
                            setFieldValue(
                              `stages[${index - 1}].semaphores`,
                              [createTrafficLightRow()]
                            );
                          }
                        }}
                        color="primary"
                        disabled={readOnly}
                      />
                    }
                    label={
                      <span className="text-[10px] text-gray-500 uppercase font-bold">
                        Tiene Poligonos
                      </span>
                    }
                  />


                  {values.stages[index - 1].semaphores?.map(
                    (semaphore, lIdx) => (
                      <div key={lIdx} className="flex flex-col items-center gap-0 mt-1">
                        {values.stages[index - 1].has_polygon && (
                          <span className="text-[10px] text-gray-500 uppercase font-bold text-center">
                            {
                              polygonZonesOptions.find(
                                (polygonZone) =>
                                  polygonZone.id === semaphore.polygon_zone_id
                              )?.name
                            }
                          </span>
                        )}
                        <TrafficLightInputs
                          key={lIdx}
                          data={semaphore}
                          readOnly={readOnly}
                          onChange={(semaphore_color_key, value) => {
                            if (readOnly) return;
                            const levelIndex = semaphore.semaphore_levels.findIndex(level => level.semaphore_color_key === semaphore_color_key);
                            setFieldValue(
                              `stages[${index - 1}].semaphores[${lIdx}].semaphore_levels[${levelIndex}].threshold_minutes`,
                              value
                            );
                          }}
                        />
                      </div>
                    )
                  )}
                  <Button
                    variant="text"
                    color=""
                    onClick={() => {
                      setMessagesModalIndex(index - 1);
                    }}

                    className=" h-6 w-fit mt-2 text-secondary-600 hover:text-secondary-400 border border-secondary-400"
                  >
                    {readOnly ? 'Ver mensajes' : 'Editar mensajes'}
                  </Button>
                </div>
              </div>
              <Circle travelStatus={travelStatus} />
            </div>
          ) : (
            <Circle travelStatus={travelStatus} />
          )}
        </div>
      ))}
      {messagesModalIndex !== null && <MessagesConfigurationsModal
        isOpen={messagesModalIndex !== null}
        onClose={() => setMessagesModalIndex(null)}
        index={messagesModalIndex ?? 0}
        readOnly={readOnly}
        channels={channels}
      />}
    </>
  );
};

export default TravelStatusTimeline;
